import { Button, Checkbox, Select, TravelInfo, Wrapper } from "components";
import { HeaderWithBack } from "components/HeaderBack";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ModalTermsTrip } from "components/Modal";
import { useFormik } from "formik";
import { useSelector } from "react-redux/es/hooks/useSelector";
import { toast } from "react-toastify";
import { getPartnerUuid } from "services/auth";
import { getDrivers } from "services/driver";
import { createTripResponse, getTripRequest } from "services/trip-request";
import { getVehicles } from "services/vehicle";
import { RootState } from "store";
import * as Yup from "yup";
import styles from "./styles.module.scss";

const TripResponseSchema = Yup.object().shape({
	vehicle: Yup.string().required("Campo obrigatório"),
	driver: Yup.string().required("Campo obrigatório"),
	acceptTerms: Yup.boolean().oneOf([true], "Você deve aceitar os termos")
});

type TripResponseFormValues = {
	vehicle: string;
	driver: string;
	acceptTerms: boolean;
};

export default function NewTravelPage() {
	const navigate = useNavigate();

	const { logged_user } = useSelector((state: RootState) => state.tokens);

	const { tripId } = useParams();
	const [trip, setTrip] = useState({});
	const [tripDeparture, setTripDeparture] = useState("");
	const [tripArrival, setTripArrival] = useState("");

	const [vehicles, setVehicles] = useState([]);
	const [drivers, setDrivers] = useState([]);
	const [showTermsModal, setShowTermsModal] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const fetchData = async () => {
		if (!logged_user) return;

		getTripRequest(tripId)
			.then((apiTrip: any) => {
				setTrip(apiTrip);

				if (apiTrip.status !== "pending") {
					navigate("/viagens");
					return;
				}

				const departureTrip = apiTrip?.points?.find(
					(point: any) => point.direction === "going" && point.type === "origin"
				);
				setTripDeparture(departureTrip.city);

				const arrivalTrip = apiTrip?.points?.find(
					(point: any) =>
						point.direction === "going" && point.type === "destination"
				);
				setTripArrival(arrivalTrip.city);
			})
			.catch((error) => {
				const message =
					"Algo saiu errado ao buscar os dados da viagem. Entre em contato com o suporte.";

				toast.error(message, {
					position: "bottom-center",
					autoClose: 5000,
					hideProgressBar: true,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					theme: "light"
				});

				navigate("/viagens");
			});

		getPartnerUuid().then((partnerId: any) => {
			getVehicles(partnerId).then(async (apiVehicles: any) => {
				setVehicles(await apiVehicles);
			});
			getDrivers(partnerId).then(async (apiDrivers: any) => {
				setDrivers(await apiDrivers);
			});
		});
	};

	useEffect(() => {
		fetchData();
	}, []);

	const formik = useFormik<TripResponseFormValues>({
		initialValues: {
			vehicle: "",
			driver: "",
			acceptTerms: false
		},
		validationSchema: TripResponseSchema,
		onSubmit: async (form) => {
			if (!tripId) return;

			setSubmitting(true);

			await createTripResponse(tripId, form.vehicle, form.driver)
				.then(() => {
					navigate("/viagens");
				})
				.catch((error) => {
					setSubmitting(false);
					let message = "Algo saiu errado. ";

					if (error.code < 500) {
						message += "Verifique os dados digitados e tente novamente.";
						if (error.body.vehicle) formik.errors.vehicle = error.body.vehicle;
						if (error.body.driver) formik.errors.driver = error.body.driver;
					} else {
						message += "Entre em contato com o suporte.";
					}

					toast.error(message, {
						position: "bottom-center",
						autoClose: 5000,
						hideProgressBar: true,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
						progress: undefined,
						theme: "light"
					});
				});
		},
		enableReinitialize: true
	});

	return (
		<div className={styles.container}>
			<ModalTermsTrip
				open={showTermsModal}
				onClose={() => setShowTermsModal(false)}
			/>

			<HeaderWithBack
				label={`${tripDeparture} → ${tripArrival}`}
				backTo={-1}
				className={styles.header}
			/>

			<Wrapper>
				<TravelInfo trip={trip} />

				<div className={styles.travelAssignment}>
					<span>Atribuir Veículo e Motorista</span>
					<form className={styles.form} onSubmit={formik.handleSubmit}>
						<div className={styles.assignmentContent}>
							<Select
								id="vehicle"
								name="vehicle"
								label="Veículo"
								placeholder="Selecione um veículo"
								value={formik.values.vehicle}
								onChange={(value) => formik.setFieldValue("vehicle", value)}
								error={formik.touched.vehicle && formik.errors.vehicle}
								errorMessage={formik.errors.vehicle}
								options={vehicles.map((vehicle: any) => ({
									value: vehicle.id,
									label: `${vehicle.vehicle_model?.name} - ${vehicle.plate_number}`
								}))}
							/>

							<Select
								id="driver"
								name="driver"
								label="Motorista"
								placeholder="Selecione um motorista"
								value={formik.values.driver}
								onChange={(value) => formik.setFieldValue("driver", value)}
								error={formik.touched.driver && formik.errors.driver}
								errorMessage={formik.errors.driver}
								options={drivers.map((driver: any) => ({
									value: driver.id,
									label: driver.name
								}))}
							/>

							<Checkbox
								name="acceptTerms"
								id="acceptTerms"
								value="1"
								label={
									<span>
										Aceito os{" "}
										<button
											type="button"
											role="link"
											onClick={() => setShowTermsModal(true)}
											style={{
												color: "#0066FF",
												background: "none",
												border: "none",
												padding: 0
											}}
										>
											Termos de Prestação de Serviços
										</button>{" "}
										e confirmo que todas as informações são verdadeiras.
									</span>
								}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								checked={formik.values.acceptTerms}
								error={formik.touched.acceptTerms && formik.errors.acceptTerms}
							/>
						</div>

						<div className={styles.buttonWrapper}>
							<Button type="submit" disabled={submitting}>
								Aceitar proposta
							</Button>
						</div>
					</form>
				</div>
			</Wrapper>
		</div>
	);
}
